import {
  Badge,
  Button,
  CustomColumnDef,
  Dropdown,
  Icons,
} from '@commenda/kakashi';
import { IWorkflowTemplate } from '@commenda/types';
import { WorkflowTemplateMetadataHelper } from './workflow-template-metadata';

interface BuildWorkflowListColumnsParams {
  showMetadataControls: boolean;
  onWorkflowEdit: (workflow: IWorkflowTemplate) => void;
  onWorkflowOrganize: (workflow: IWorkflowTemplate) => void;
  onWorkflowDelete: (workflow: IWorkflowTemplate) => void;
}

export const buildWorkflowListColumns = ({
  showMetadataControls,
  onWorkflowEdit,
  onWorkflowOrganize,
  onWorkflowDelete,
}: BuildWorkflowListColumnsParams): CustomColumnDef<
  IWorkflowTemplate,
  unknown
>[] => [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (row) => row.name,
    cell: ({ row }) => (
      <span className="txt-small-medium text-text-primary">
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'externalName',
    header: 'External Name',
    accessorFn: (row) => row.externalName,
    cell: ({ row }) => (
      <span className="text-text-secondary">{row.original.externalName}</span>
    ),
  },
  ...(showMetadataControls
    ? ([
        {
          id: 'category',
          header: 'Category',
          accessorFn: (row) =>
            WorkflowTemplateMetadataHelper.getCategory(row) ?? '',
          cell: ({ row }) => {
            const category = WorkflowTemplateMetadataHelper.getCategory(
              row.original
            );
            if (!category) {
              return (
                <span className="text-text-tertiary txt-small-regular">-</span>
              );
            }
            return (
              <Badge size="sm" variant="blue">
                {WorkflowTemplateMetadataHelper.CATEGORY_LABELS[category]}
              </Badge>
            );
          },
        },
        {
          id: 'tags',
          header: 'Tags',
          accessorFn: (row) =>
            WorkflowTemplateMetadataHelper.getTags(row).join(' '),
          cell: ({ row }) => {
            const tags = WorkflowTemplateMetadataHelper.getTags(row.original);
            if (tags.length === 0) {
              return (
                <span className="text-text-tertiary txt-small-regular">-</span>
              );
            }
            return (
              <div className="flex flex-wrap gap-xs">
                {tags.map((tag) => (
                  <Badge key={tag} size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            );
          },
        },
      ] satisfies CustomColumnDef<IWorkflowTemplate, unknown>[])
    : []),
  {
    id: 'createdBy',
    header: 'Created By',
    accessorFn: (row) => row.createdByUserName,
    cell: ({ row }) => (
      <span className="text-text-secondary">
        {row.original.createdByUserName ?? '-'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    accessorFn: () => null,
    width: 65,
    showInSelector: false,
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          preventAutoFocusOnClose
          align="end"
          menu={{
            groups: [
              {
                items: [
                  {
                    label: 'Edit',
                    onClick: () => onWorkflowEdit(row.original),
                  },
                  ...(showMetadataControls
                    ? [
                        {
                          label: 'Organize',
                          onClick: () => onWorkflowOrganize(row.original),
                        },
                      ]
                    : []),
                  {
                    label: 'Delete',
                    onClick: () => onWorkflowDelete(row.original),
                    labelClassName: 'text-text-error',
                  },
                ],
              },
            ],
          }}
        >
          <Button variant="tertiary" size="sm" aria-label="Workflow actions">
            <Icons.DotsVertical className="size-4 text-text-quaternary" />
          </Button>
        </Dropdown>
      </div>
    ),
  },
];
